import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import api from 'api';
import useQueryParams from 'hooks/useQueryParams';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import useTableFetchState from 'hooks/useTableFetchState';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import MultiFilterPanel from 'components/MultiFilterPanel';
import MultiSortPanel from 'components/MultiSortPanel';
import ManageColumns from 'components/ManageColumns';
import TableExportPanel from 'components/TableExportPanel';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import { getTableExportRowsAndColumnsParams } from 'components/TableExportPanel/TableExportPanel.utils';
import { TableExportSelection } from 'models/TableExport';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import { SellerConnectSellersFilters } from 'models/SellerConnectEngine';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DownloadStatusContext from 'providers/DownloadStatus/DownloadStatus.context';
import downloadService from 'services/downloadService';
import { getArrayLength, getHasActiveFilters, getIsArrayEmpty } from 'utils';
import SellersTable from './components/SellersTable';
import {
  COLUMNS,
  DEFAULT_SORT,
  HEADING_TRANSLATIONS,
  LOCKED_COLUMN,
  SellersTableData,
} from './components/SellersTable/data';
import useSellersFilters from './hooks/useSellersFilters';
import { getSellersTableRows } from './utils';

import './Sellers.styles.scss';

type SellersProps = {
  className?: string;
};

const Sellers: React.FC<SellersProps> = (props) => {
  const { className } = props;

  const { t } = useTranslation();

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    isBossModeExportInProgress,
    startBossModeExportDownload,
    failBossModeExportDownload,
  } = useContext(DownloadStatusContext);

  const {
    isLoading,
    totalPages,
    totalItems,
    data: items,
    setIsLoading,
    setPaginatedData,
  } = useTableFetchState<SellersTableData>();

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const {
    params,
    params: {
      page,
      sort = DEFAULT_SORT,
      name,
      business,
      industry,
      state,
      revenueRange,
      mode,
      intakeStatus,
      submittedTimeframe,
    },
  } = useQueryParams();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const {
    filterOptions,
    translatedSortOptions,
    multiSortDirectionTranslation,
    columnFilterOptions,
  } = useSellersFilters();

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.SELLER_CONNECT_LIST,
    COLUMNS,
  );

  const classes = classNames('sellers', className);

  const isEmpty = !isLoading && getIsArrayEmpty(items);

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, params),
    [filterOptions, params],
  );

  const filters = useMemo<SellerConnectSellersFilters>(
    () => ({
      name,
      business,
      industry,
      state,
      revenueRange,
      mode,
      intakeStatus,
      submittedTimeframe,
    }),
    [
      name,
      business,
      industry,
      state,
      revenueRange,
      mode,
      intakeStatus,
      submittedTimeframe,
    ],
  );

  const getResultsCount = useCallback(
    async (filterParams: SellerConnectSellersFilters) => {
      const { data } = await api.sellerConnectEngine.getSellerConnectSellers({
        ...filterParams,
        $limit: '1',
      });

      return data.totalItems;
    },
    [],
  );

  const onExport = useCallback(
    async ({ rows, columns: columnsScope, format }: TableExportSelection) => {
      try {
        startBossModeExportDownload();

        await api.sellerConnectEngine.exportSellerConnectSellers({
          ...filters,
          $sort: sort,
          ...getTableExportRowsAndColumnsParams({
            rows,
            columnsScope,
            columns,
            page,
            limit,
          }),
          format,
          uniqueId: downloadService.downloadUniqueId,
        });
      } catch (e) {
        popServerError(e);
        failBossModeExportDownload();
      }
    },
    [
      filters,
      sort,
      columns,
      page,
      limit,
      popServerError,
      startBossModeExportDownload,
      failBossModeExportDownload,
    ],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const { data } = await api.sellerConnectEngine.getSellerConnectSellers({
          $page: page,
          $limit: limit,
          $sort: sort,
          ...filters,
        });

        setPaginatedData({ ...data, items: getSellersTableRows(data.items) });
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    page,
    limit,
    sort,
    filters,
    popServerError,
    setIsLoading,
    setPaginatedData,
  ]);

  return (
    <div className={classes}>
      <div className="admin-dashboard-router__heading">
        <h1>{t('sellers')}</h1>

        <div className="sellers__controls">
          {(!isEmpty || hasActiveFilters) && (
            <>
              {!isEmpty && (
                <NumberOfResultsPerPageFilter
                  limit={limit}
                  updateLimit={updateLimit}
                />
              )}
              <MultiFilterPanel
                ref={multiFilterRef}
                filters={filterOptions}
                getResultsCount={getResultsCount}
              />
              <MultiSortPanel
                ref={multiSortRef}
                options={translatedSortOptions}
                sortDirectionTranslations={multiSortDirectionTranslation}
              />
            </>
          )}
          <ManageColumns
            allColumns={allColumns}
            hiddenColumns={hiddenColumns}
            headingTranslations={HEADING_TRANSLATIONS}
            lockedColumn={LOCKED_COLUMN}
            isDefaultColumns={isDefaultColumns}
            onSave={saveColumnSettings}
            onResetToDefault={resetToDefault}
          />
          <TableExportPanel
            totalItems={totalItems}
            pageItemsCount={getArrayLength(items)}
            totalPages={totalPages}
            totalColumnsCount={getArrayLength(COLUMNS)}
            visibleColumnsCount={getArrayLength(columns)}
            isExporting={isBossModeExportInProgress}
            onExport={onExport}
          />
        </div>
      </div>

      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={translatedSortOptions}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      <SellersTable
        isLoading={isLoading}
        data={items}
        sort={sort}
        totalPages={totalPages}
        totalItems={totalItems}
        columns={columns}
        columnFilterOptions={columnFilterOptions}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        onReorder={onReorder}
      />
    </div>
  );
};

export default Sellers;
